import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { resourcesSchema, ResourcesFormData } from "@pma/utils/work-diaries-schema"
import { DictType } from "@pma/types"
import axios from "axios"
import {
  DICTIONARY_RESOURCES_ADD_ENDPOINT,
  DICTIONARY_RESOURCES_EDIT_ENDPOINT,
  SERVER_URL,
  WORK_DIARY_RESOURCES_ADD_ENDPOINT,
  WORK_DIARY_RESOURCES_EDIT_ENDPOINT,
} from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { ResourcesForm } from "./resources-form"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { useEffect, useState } from "react"
import { Button } from "../../button"

interface ResourcesStepProps {
  workDiaries: DictType | undefined
  project?: DictType
  lstStructures: any[]
  lstWorkforce: any[]
  lstTools: any[]
  refetchWorkDiary: () => void
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
}

export function ResourcesStep({
  workDiaries,
  project,
  lstStructures,
  lstWorkforce,
  lstTools,
  setActiveStep,
}: ResourcesStepProps) {
  const queryClient = useQueryClient()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [firstTime, setFirstTime] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ResourcesFormData>({
    resolver: zodResolver(resourcesSchema(translate)),
  })

  useEffect(() => {
    if (firstTime && workDiaries != undefined) {
      setFirstTime(false)
      form.reset({
        siteResources: workDiaries?._diary_resourses
          .filter((obj: any) => obj?._resource?.structures)
          .map((obj: any) => {
            return {
              id: obj.id,
              resource_id: obj.resource_id,
              typeName: obj.resource_id,
              remarks: obj?.remarks,
              quantity: obj.amount,
              type: "structures",
            }
          }),
        workforce: workDiaries?._diary_resourses
          .filter((obj: any) => obj?._resource?.employees)
          .map((obj: any) => {
            return {
              id: obj.id,
              resource_id: obj.resource_id,
              typeName: obj.resource_id,
              remarks: obj?.remarks,
              quantity: obj.amount,
              type: "employees",
            }
          }),
        equipment: workDiaries?._diary_resourses
          .filter((obj: any) => obj?._resource?.tools)
          .map((obj: any) => {
            return {
              id: obj.id,
              resource_id: obj.resource_id,
              typeName: obj.resource_id,
              remarks: obj?.remarks,
              quantity: obj.amount,
              type: "tools",
            }
          }),
      })
    }
  }, [workDiaries])

  const onSubmit = async (data: ResourcesFormData) => {
    setIsSubmitting(true)
    const allResources = [
      ...data.siteResources.map((r) => ({ ...r, type: "structures" })),
      ...data.workforce.map((r) => ({ ...r, type: "employees" })),
      ...data.equipment.map((r) => ({ ...r, type: "tools" })),
    ]

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }

    const isError = []

    for (const res of allResources) {
      let response = ""
      try {
        if (res.actionType === "delete") {
          await ditchResourceToDiary(res.id)
        } else if (typeof res.typeName === "string") {
          const dictResource = await createDictionaryResource(res, headers)
          response = await handleAttachOrUpdateResource(res, dictResource)
        } else {
          const existingResource = findExistingResource(res.typeName, res.type)
          if (res.translatedName && existingResource) {
            await updateDictionaryResourceTranslation(existingResource, res.translatedName)
          }
          response = await handleAttachOrUpdateResource(res, existingResource)
        }
        if (response != undefined) {
          toast({
            title: translate("Success"),
            description: translate("Resources updated and move Next Tab"),
            variant: "success",
          })
        } else isError.push(true)
        setIsSubmitting(false)
      } catch (err: any) {
        isError.push(true)
        setIsSubmitting(false)
        const message = err?.response?.data?.message
        toast({
          title: translate("Error"),
          description: message,
          variant: "error",
        })
      }
    }

    if (!isError.includes(true)) {
      await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
      await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] }) // <- Called only AFTER everything completes
      await queryClient.invalidateQueries({ queryKey: ["resources-structures-context"] })
      await queryClient.invalidateQueries({ queryKey: ["resources-workforce-context"] })
      await queryClient.invalidateQueries({ queryKey: ["resources-tools-context"] })
      setActiveStep(2)
    }
  }

  const updateDictionaryResourceTranslation = async (
    existingResource: any,
    translatedName: string,
  ) => {
    const isHebrew = locale === "he"
    await axios.post(
      SERVER_URL + DICTIONARY_RESOURCES_EDIT_ENDPOINT,
      {
        organization_id: project?.organization_user_id,
        resources_id: existingResource.id,
        en_name: isHebrew ? translatedName : existingResource.en,
        he_name: isHebrew ? existingResource.he : translatedName,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
  }

  const createDictionaryResource = async (res: any, headers: any) => {
    const isHebrew = locale === "he"
    const translatedName = res.translatedName || res.typeName

    const response = await axios.post(
      SERVER_URL + DICTIONARY_RESOURCES_ADD_ENDPOINT,
      {
        organization_id: project?.organization_user_id,
        en_name: isHebrew ? translatedName : res.typeName,
        he_name: isHebrew ? res.typeName : translatedName,
        [res.type]: true,
        type: res.type,
      },
      { headers },
    )
    return response.data
  }

  const handleAttachOrUpdateResource = async (res: any, dictResource: any) => {
    const payload = {
      ...dictResource,
      quantity: res.quantity,
      remarks: res.remarks,
    }
    let response

    if (res.actionType === "add" && !res.id) {
      const bodyAdd = {
        diary_id: workDiaries?.id,
        resources_id: payload?.id,
        amount: payload?.quantity,
        remarks: payload?.remarks,
        type: res.type,
        // [res.type]: true,
      }
      response = await attachResourceToDiary(bodyAdd)
    } else if (res.id && res.resource_id) {
      const bodyEdit = {
        diary_id: workDiaries?.id,
        diary_resources_id: res.id,
        dictionary_resource_id: payload?.id,
        amount: payload?.quantity,
        remarks: payload?.remarks,
      }
      response = await attachResourceToDiary(bodyEdit, true)
    }

    return response?.data
  }

  const findExistingResource = (id: any, type: string) => {
    if (type === "structures") {
      return lstStructures.find((obj) => obj.id === id)
    } else if (type === "employees") {
      return lstWorkforce.find((obj) => obj.id === id)
    } else if (type === "tools") {
      return lstTools.find((obj) => obj.id === id)
    }
  }

  // Add or Edit: call same API (assumes upsert behavior)
  const attachResourceToDiary = async (body: any, isEdit: boolean = false) => {
    const url = isEdit
      ? SERVER_URL + WORK_DIARY_RESOURCES_EDIT_ENDPOINT
      : SERVER_URL + WORK_DIARY_RESOURCES_ADD_ENDPOINT
    try {
      return await axios.post(url, body, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      })
    } catch (err: any) {
      const message = err?.response?.data?.message
      toast({
        title: translate("Error"),
        description: message,
        variant: "error",
      })
    }
  }

  const ditchResourceToDiary = async (diary_resourceId: number | undefined) => {
    return await axios.delete(`${SERVER_URL}/diary/resources/delete/${diary_resourceId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <ResourcesForm
          lstStructures={lstStructures}
          lstWorkforce={lstWorkforce}
          lstTools={lstTools}
        />
        <div className='flex flex-col justify-between gap-4 border-t pt-6 sm:flex-row'>
          <Button
            type='button'
            variant='outline'
            className='w-full sm:w-auto'
            onClick={() => setActiveStep(0)}
          >
            {translate("Previous")}
          </Button>
          <Button
            type='submit'
            className='w-full sm:w-auto'
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {`${translate("Save")} & ${translate("Next")}`}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
